/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect } from "react";
import { useTranslation as useTranslationI18n } from "react-i18next";
import { Direction, LanguageConfig, SupportedLanguage } from "i18n/types";

/**
 * Custom hook for managing text direction based on current language
 * Automatically updates document direction and language attributes
 */
export const useDirection = (): {
  direction: Direction;
  isRTL: boolean;
  language: SupportedLanguage;
} => {
  const { i18n } = useTranslationI18n();
  const currentLanguage =
    (i18n.language as SupportedLanguage) || SupportedLanguage.EN;
  const direction = LanguageConfig[currentLanguage]?.direction || "ltr";
  const isRTL = direction === "rtl";

  useEffect(() => {
    // Update document direction
    document.documentElement.dir = direction;
    document.documentElement.lang = currentLanguage;

    // Update body class for RTL styling if needed
    if (isRTL) {
      document.body.classList.add("rtl");
      document.body.classList.remove("ltr");
    } else {
      document.body.classList.add("ltr");
      document.body.classList.remove("rtl");
    }
  }, [direction, currentLanguage, isRTL]);

  return {
    direction,
    isRTL,
    language: currentLanguage,
  };
};
